import { useRef } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { CheckCircle, Users, Target, Lightbulb, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';

const highlights = [
  { icon: Users, value: '50+', label: 'Team Members' },
  { icon: Target, value: '99%', label: 'Client Satisfaction' },
  { icon: Lightbulb, value: '200+', label: 'Solutions Built' },
];

const values = [
  'Client-Centric Approach',
  'Agile Development Process',
  'Transparent Communication',
  'Quality-First Mindset',
  'Long-Term Partnerships',
  'Continuous Innovation',
];

export default function AboutSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const navigate = useNavigate();
  const parallaxRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: parallaxRef, offset: ['start end', 'end start'] });
  const imageY = useTransform(scrollYProgress, [0, 1], ['0%', '20%']);
  const contentX = useTransform(scrollYProgress, [0, 0.5, 1], ['30px', '0px', '-10px']);

  return (
    <section id="about" ref={parallaxRef} data-testid="about-section" className="relative py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-slate-950" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" />
      {/* Floating orbs */}
      <motion.div
        animate={{ y: [0, -25, 0] }}
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute top-1/3 right-0 w-80 h-80 bg-blue-500/3 rounded-full blur-[120px]"
      />

      <div ref={ref} className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left - Image with enhanced parallax */}
          <motion.div
            initial={{ opacity: 0, x: -50, scale: 0.95 }}
            animate={isInView ? { opacity: 1, x: 0, scale: 1 } : {}}
            transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
              <motion.img
                style={{ y: imageY }}
                src="https://images.pexels.com/photos/5511303/pexels-photo-5511303.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Modern conference room"
                className="w-full h-[130%] object-cover -mt-[15%]"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-slate-950/50 to-transparent" />
            </div>
            {/* Decorative border glow */}
            <motion.div
              animate={{ opacity: [0.3, 0.6, 0.3] }}
              transition={{ duration: 4, repeat: Infinity }}
              className="absolute -inset-1 rounded-2xl bg-gradient-to-r from-blue-500/20 via-transparent to-cyan-500/20 -z-10 blur-sm"
            />
            {/* Floating stats card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.5 }}
              whileHover={{ scale: 1.03 }}
              className="absolute -bottom-6 -right-4 md:right-8 bg-slate-900/80 backdrop-blur-xl border border-white/10 rounded-xl p-5 shadow-xl"
            >
              <div className="flex items-center gap-4">
                {highlights.map((item, i) => (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, y: 10 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.7 + i * 0.15 }}
                    className="text-center px-3"
                  >
                    <div className="text-xl font-heading font-bold text-white">{item.value}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{item.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>

          {/* Right - Content */}
          <motion.div style={{ x: contentX }}>
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              <span className="text-sm font-medium text-blue-400 tracking-widest uppercase mb-4 block">
                About Softlume
              </span>
              <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight text-white mb-6">
                Engineering Excellence,{' '}
                <span className="gradient-text-blue">Delivered</span>
              </h2>
              <p className="text-base text-slate-400 leading-relaxed mb-6">
                Since our inception, Softlume has been at the forefront of digital innovation.
                We combine deep technical expertise with a passion for creating solutions that
                make a real impact on businesses worldwide.
              </p>
              <p className="text-base text-slate-400 leading-relaxed mb-8">
                Our team of engineers, designers, and strategists work collaboratively to
                transform complex challenges into elegant, scalable solutions.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {values.map((value, index) => (
                  <motion.div
                    key={value}
                    initial={{ opacity: 0, x: 20 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.4, delay: 0.4 + index * 0.08 }}
                    className="flex items-center gap-3 group"
                  >
                    <CheckCircle className="w-4 h-4 text-blue-400 flex-shrink-0 group-hover:scale-110 transition-transform" />
                    <span className="text-sm text-slate-300">{value}</span>
                  </motion.div>
                ))}
              </div>

              <Button
                data-testid="about-explore-more"
                onClick={() => navigate('/about')}
                className="bg-transparent border border-white/10 text-slate-300 hover:text-white hover:border-blue-500/40 hover:bg-blue-500/5 rounded-full px-6 h-10 text-sm font-medium transition-all duration-300 group"
              >
                Learn More About Us
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
